import { Presentation } from 'lucide-react'
import { GRACKLE_COLORS } from '@/lib/theme'
import { WorksheetScreen } from '@/components/training/WorksheetScreen'
import type { SheetColumn } from '@/components/training/SheetTable'
import type { TrainingWorksheetRow, TrainingVocabularies } from '@/types'

/**
 * The "Purchased Webinars" tab — sold webinars.
 *
 * The only tab with a genuine date *and* a time, so `webinar_date` is a real DATE
 * and gets a date picker. The time stays text: the source writes it as "11am ET"
 * and "1:00 PM Eastern", which is a timezone claim a TIME column cannot hold.
 *
 * A calendar view belongs here eventually — this is the one tab that could support
 * one. Listed first, per the decision recorded in the plan.
 */

// The three the sheet offers. "Enquiry", "Booked" and "Cancelled" were invented here.
// This tab title-cases its statuses where Training Tracking lower-cases them; that
// inconsistency is the sheet's own and is kept so stored values stay selectable.
const STATUS_TINTS: Record<string, string> = {
  Completed: GRACKLE_COLORS.pistachio,
  'In Progress': GRACKLE_COLORS.pollen,
  'Not Started': GRACKLE_COLORS.powderBlue,
}

const columns = (vocab?: TrainingVocabularies): SheetColumn[] => {
  const yesNo = vocab?.yes_no ?? []
  return [
    { key: 'account_name', label: 'CRM account', kind: 'readonly', width: 'min-w-[15rem]' },
    { key: 'client_name', label: 'Client', kind: 'text', width: 'min-w-[15rem]' },
    { key: 'poc_name', label: 'POC name', kind: 'text', width: 'min-w-[11rem]' },
    { key: 'poc_email', label: 'POC email', kind: 'text', width: 'min-w-[14rem]' },
    {
      // Nine named webinars. The sheet validates this too, but its list is a
      // comma-separated literal, so the one title containing commas appears there as
      // three separate broken options. Stored whole here.
      key: 'webinar_type',
      label: 'Webinar type',
      kind: 'select',
      options: vocab?.webinar_type ?? [],
      width: 'min-w-[20rem]',
    },
    {
      key: 'duration_hours_text',
      label: 'Duration',
      kind: 'text',
      width: 'min-w-[9rem]',
      note: 'free text in the sheet',
    },
    {
      key: 'billed_free',
      label: 'Billed/Free',
      kind: 'select',
      options: vocab?.billed_free ?? [],
      width: 'min-w-[8rem]',
    },
    {
      key: 'recording_needed',
      label: 'Recording needed',
      kind: 'select',
      options: yesNo,
      width: 'min-w-[9rem]',
    },
    {
      key: 'grackle_will_caption',
      label: 'Grackle will caption',
      kind: 'select',
      options: yesNo,
      width: 'min-w-[9rem]',
    },
    { key: 'sales_rep_name', label: 'Sales rep', kind: 'text', width: 'min-w-[11rem]' },
    { key: 'sme_presenter', label: 'SME presenter', kind: 'text', width: 'min-w-[11rem]' },
    {
      key: 'status',
      label: 'Status',
      kind: 'select',
      options: vocab?.webinar_status ?? [],
      width: 'min-w-[10rem]',
    },
    { key: 'webinar_date', label: 'Webinar date', kind: 'date', width: 'min-w-[9.5rem]' },
    {
      key: 'webinar_time_text',
      label: 'Webinar time',
      kind: 'text',
      width: 'min-w-[11rem]',
      note: 'includes the timezone',
    },
    { key: 'notes', label: 'Notes', kind: 'longtext', width: 'min-w-[20rem]' },
  ]
}

export default function TrainingWebinars() {
  return (
    <WorksheetScreen
      sheet="webinars"
      title="Webinars"
      description="Purchased webinars. The date is a real date; the time keeps its timezone as written."
      icon={<Presentation className="h-6 w-6" aria-hidden="true" />}
      columns={columns}
      tints={STATUS_TINTS}
      tintFor={(r: TrainingWorksheetRow) => STATUS_TINTS[String(r.status ?? '')]}
      rowLabel={(r) => String(r.client_name ?? r.account_name ?? 'this client')}
      caption="Purchased webinars by client, with date, time, captioning and presenter. Rows are tinted by status; the colour key is below the table."
      searchPlaceholder="Search client, webinar type or notes..."
    />
  )
}
